import { useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { InvoiceResponse } from '../../api/types';
import { getInvoicePdf, type InvoicePdfVariant } from '../../api/endpoints/invoicePdfs';
import { invoicePdfQueue } from './invoicePdfUpload';
import styles from '../payments/Payments.module.css';
import { Button } from '../../components/ui/Button';
export function StoredInvoicePdf({
  invoice,
  variant = invoice.status === 'CANCELLED' ? 'CANCELLED' : 'ORIGINAL',
}: {
  invoice: InvoiceResponse;
  variant?: InvoicePdfVariant;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const state = useSyncExternalStore(invoicePdfQueue.subscribe, () =>
    invoicePdfQueue.status(invoice.id, variant),
  );
  const queryKey = ['payments', 'invoice-pdf', invoice.id, variant];
  const pdf = useQuery({
    queryKey,
    queryFn: () => getInvoicePdf(invoice.id, variant),
    retry: false,
    staleTime: 60_000,
  });
  const ready = pdf.data?.status === 'READY' || state === 'READY';
  const preparing = state === 'PREPARING';
  const originalArchive = invoice.status === 'CANCELLED' && variant === 'ORIGINAL';
  return (
    <div className={styles.orderActions}>
      {ready ? (
        <Button
          variant="secondary"
          size="sm"
          disabled={opening}
          onClick={async () => {
            // Open synchronously to preserve the browser's user gesture; request a new
            // short-lived URL each time, so an old query result never opens an expired URL.
            const destination = window.open('about:blank', '_blank');
            if (destination) destination.opener = null;
            setOpening(true);
            setError(null);
            try {
              const current = await getInvoicePdf(invoice.id, variant);
              if (current.status !== 'READY' || !current.downloadUrl)
                throw new Error('PDF missing');
              if (!destination) throw new Error('Pop-up blocked');
              destination.location.replace(current.downloadUrl);
            } catch {
              destination?.close();
              setError('Could not open stored PDF. Try again.');
              await client.invalidateQueries({ queryKey });
            } finally {
              setOpening(false);
            }
          }}
        >
          {opening
            ? 'Opening PDF…'
            : originalArchive
              ? 'View / Download original PDF'
              : 'View / Download PDF'}
        </Button>
      ) : (
        <>
          <span role="status" className={`${styles.statusChip} ${styles.amber}`}>
            {originalArchive ? 'Original: ' : ''}
            {preparing ? 'Preparing PDF…' : pdf.isPending ? 'Checking PDF…' : 'PDF missing'}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={preparing || pdf.isPending}
            onClick={async () => {
              setError(null);
              await invoicePdfQueue.ensure(invoice, variant);
              await client.invalidateQueries({ queryKey });
            }}
          >
            {originalArchive ? 'Generate original PDF' : 'Generate PDF'}
          </Button>
        </>
      )}
      {pdf.isError && (
        <span role="alert">
          {t('invoices.storedInvoicePdf.checkFailed')}{' '}
          <Button variant="secondary" size="sm" onClick={() => pdf.refetch()}>
            {t('common.actions.retry')}
          </Button>
        </span>
      )}
      {error && <span role="alert">{error}</span>}
    </div>
  );
}
